import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import CloseIcon from 'mdi-react/CloseIcon';
import { isDarkTheme } from '../../helpers/themeTableStyles';
import OnboardingSettingsPanel from './OnboardingSettingsPanel';
import { getOnboardingSettingsConfig } from './onboardingSettingsConfig';

/**
 * OnboardingSettingsModal - Reusable trigger button + wide modal overlay that
 * shows the same "discover / confirm" settings picker used on the ad
 * platform OAuth success page (see OnboardingSettingsPanel). Intended for
 * places like /admin/account/status where an admin/CS user wants to review
 * or update a customer's already-connected platform settings.
 *
 * Uses the same "wide" modal pattern as AccountStatusAll's Live Status modal
 * (size="xl" + maxWidth: 95vw + ModalHeader/ModalBody) so it makes use of
 * the available screen space, rather than reactstrap's cramped default
 * modal-dialog width.
 *
 * Only renders the trigger button (and can be used) when the platform has a
 * registered config in onboardingSettingsConfig.js — this keeps it a no-op
 * for platforms that haven't been built out yet (currently only 'meta').
 */
const OnboardingSettingsModal = ({
  platform, accountId, buttonLabel, buttonColor, buttonSize, buttonClassName, onSettingsSaved,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const user = useSelector(state => state.authentication.user);
  const theme = useSelector(state => state.theme);
  const darkMode = isDarkTheme(theme);

  const settingsConfig = getOnboardingSettingsConfig(platform);
  if (!settingsConfig || !accountId) return null;

  const toggle = () => setIsOpen(prev => !prev);
  const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);

  // On a successful confirm: close the overlay and let the caller (e.g. the
  // account status page) know so it can re-run its status check.
  const handleConfirmSuccess = () => {
    setIsOpen(false);
    onSettingsSaved?.();
  };

  return (
    <>
      <Button
        color={buttonColor}
        size={buttonSize}
        className={buttonClassName}
        onClick={toggle}
      >
        {buttonLabel || `Update ${platformName} Settings`}
      </Button>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="xl"
        className={theme.className}
        style={{ maxWidth: '95vw' }}
      >
        <ModalHeader tag="div" close={<span />}>
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="close"
              onClick={toggle}
              aria-label="Close"
              style={{ marginRight: '12px' }}
            >
              <CloseIcon size={20} style={{ color: darkMode ? '#dddddd' : '#1c1c1c' }} />
            </button>
            <span>{platformName} Account Settings</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ minHeight: '60vh' }}>
          <OnboardingSettingsPanel
            platform={platform}
            accountId={accountId}
            token={user?.jwtToken || null}
            fieldsConfig={settingsConfig}
            platformDisplayName={platformName}
            onConfirmSuccess={handleConfirmSuccess}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

OnboardingSettingsModal.propTypes = {
  platform: PropTypes.string.isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  buttonLabel: PropTypes.string,
  buttonColor: PropTypes.string,
  buttonSize: PropTypes.string,
  buttonClassName: PropTypes.string,
  // Called (no args) after settings are successfully saved and the modal
  // has closed itself -- use this to re-run any status checks.
  onSettingsSaved: PropTypes.func,
};

OnboardingSettingsModal.defaultProps = {
  buttonLabel: null,
  buttonColor: 'primary',
  buttonSize: 'sm',
  buttonClassName: '',
  onSettingsSaved: null,
};

export default OnboardingSettingsModal;

